import { useContext, useCallback } from 'react';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import events, {
  APPLICATIONS_WORK_TYPE_LABELS,
  APPLICATIONS_RECORD_SOURCE_LABELS,
  STARTING_RECORD_TYPE_LABELS
} from 'utilities/amplitude/events';
import formatAmplitudeError from 'utilities/amplitude/helpers';
import { OperationType } from 'screens/Applications&WorkOrders/RouteAdmin';
import { LayoutStrings } from '../StepsLayout/Constants/layoutStrings';

const STEP_LABEL_MAP: Record<string, string> = {
  [LayoutStrings.stepNames.application.details]: 'Step 1',
  [LayoutStrings.stepNames.workOrder.details]: 'Step 1',
  [LayoutStrings.stepNames.selectProperties]: 'Step 2',
  [LayoutStrings.stepNames.selectProducts]: 'Step 3',
  [LayoutStrings.stepNames.weather]: 'Step 4'
};

const getStepLabel = (stepName?: string): string =>
  stepName ? STEP_LABEL_MAP[stepName] || stepName : 'Step 1';

const awEvents = events.epic.ApplicationsAndWorkOrders;

const eventMap = {
  application: {
    access: awEvents.accessApplications,
    initiateCreate: awEvents.initiateCreateApplication,
    initiateEdit: awEvents.initiateEditApplication,
    view: awEvents.viewApplication,
    applyFilters: awEvents.applyApplicationFilters,
    delete: awEvents.deleteApplication,
    initiateDuplicate: awEvents.initiateDuplicateApplication,
    printPdf: awEvents.printApplicationPdf,
    downloadPdf: awEvents.downloadApplicationPdf,
    save: awEvents.saveApplication,
    saveEdits: awEvents.saveApplicationEdits
  },
  workOrder: {
    access: awEvents.accessWorkOrders,
    initiateCreate: awEvents.initiateCreateWorkOrder,
    initiateEdit: awEvents.initiateEditWorkOrder,
    view: awEvents.viewWorkOrder,
    applyFilters: awEvents.applyWorkOrderFilters,
    delete: awEvents.deleteWorkOrder,
    initiateDuplicate: awEvents.initiateDuplicateWorkOrder,
    printPdf: awEvents.printWorkOrderPdf,
    downloadPdf: awEvents.downloadWorkOrderPdf,
    save: awEvents.saveWorkOrder,
    saveEdits: awEvents.saveWorkOrderEdits
  }
};

const useOperationEvents = (operationType: OperationType) => {
  const { sendEventToAmplitude } = useContext(AmplitudeContext);
  const key =
    operationType === OperationType.APPLICATION ? 'application' : 'workOrder';
  const opEvents = eventMap[key];
  const operationLabel =
    operationType === OperationType.APPLICATION
      ? APPLICATIONS_RECORD_SOURCE_LABELS.APPLICATION
      : APPLICATIONS_RECORD_SOURCE_LABELS.WORK_ORDER;

  const sendInitiateCreateEvent = useCallback(() => {
    sendEventToAmplitude?.(opEvents.initiateCreate);
  }, [sendEventToAmplitude, opEvents]);

  const sendInitiateEditEvent = useCallback(() => {
    sendEventToAmplitude?.(opEvents.initiateEdit);
  }, [sendEventToAmplitude, opEvents]);

  const sendViewEvent = useCallback(() => {
    sendEventToAmplitude?.(opEvents.view);
  }, [sendEventToAmplitude, opEvents]);

  const sendApplyFiltersEvent = useCallback(
    (dateRangeAdjusted: boolean) => {
      sendEventToAmplitude?.(opEvents.applyFilters, {
        'Date range adjusted': dateRangeAdjusted
      });
    },
    [sendEventToAmplitude, opEvents]
  );

  const sendDeleteEvent = useCallback(
    (result: string, apiLengthMs: number, error?: unknown) => {
      sendEventToAmplitude?.(opEvents.delete, {
        [`Delete ${operationLabel} result`]: `${result} (${apiLengthMs} ms)`,
        Error: result === 'Error' && error ? formatAmplitudeError(error) : 'N/A'
      });
    },
    [sendEventToAmplitude, opEvents, operationLabel]
  );

  const sendInitiateDuplicateEvent = useCallback(
    (buttonLocation: 'Confirmation toast' | 'Detail View menu') => {
      sendEventToAmplitude?.(opEvents.initiateDuplicate, {
        'Button location': buttonLocation
      });
    },
    [sendEventToAmplitude, opEvents]
  );

  const sendPrintPdfEvent = useCallback(
    (
      orientation: string,
      buttonLocation: 'PDF Settings modal' | 'Confirmation toast',
      language?: string
    ) => {
      sendEventToAmplitude?.(opEvents.printPdf, {
        Orientation: orientation,
        'Button location': buttonLocation,
        ...(key === 'workOrder' &&
          language && {
            Language: language
          })
      });
    },
    [sendEventToAmplitude, opEvents, key]
  );

  const sendDownloadPdfEvent = useCallback(
    (orientation: string, language?: string) => {
      sendEventToAmplitude?.(opEvents.downloadPdf, {
        Orientation: orientation,
        ...(key === 'workOrder' &&
          language && {
            Language: language
          })
      });
    },
    [sendEventToAmplitude, opEvents, key]
  );

  const sendSaveEvent = useCallback(
    (
      result: string,
      apiCallLengthMs: number,
      formValues: any,
      isDuplicateFlow: boolean,
      error?: unknown
    ) => {
      const carrierEnabled =
        Boolean(formValues?.isTankInfoEnabled) &&
        (formValues?.products?.some(
          (product: any) => product.isCarrier && !product.isHidden
        ) ??
          false);

      const recordSourceType = formValues.recordSource?.recordSourceType;
      const trackableRecordSource = recordSourceType
        ? APPLICATIONS_RECORD_SOURCE_LABELS[
            recordSourceType as keyof typeof APPLICATIONS_RECORD_SOURCE_LABELS
          ]
        : undefined;

      const errorMessage =
        result === 'Error' && error ? formatAmplitudeError(error) : 'N/A';

      sendEventToAmplitude?.(opEvents.save, {
        [`Save ${operationLabel} result`]: `${result} (${apiCallLengthMs} ms)`,
        Error: errorMessage,
        'Work Type':
          APPLICATIONS_WORK_TYPE_LABELS[
            formValues.workType as keyof typeof APPLICATIONS_WORK_TYPE_LABELS
          ] || 'General',
        'Number of equipment': formValues.equipments?.length ?? 0,
        'Number of tags': formValues.tagIds?.length ?? 0,
        'Number of crop zones': formValues.cropZones?.length ?? 0,
        'Number of products':
          formValues.products?.filter(
            (product: any) => !product.isCarrier && !product.isHidden
          )?.length ?? 0,
        'Tank Info': formValues.isTankInfoEnabled ? 'On' : 'Off',
        Carrier: carrierEnabled ? 'On' : 'Off',
        'Automatic weather':
          formValues.weather?.mode === 'AUTOMATIC' ? 'On' : 'Off',
        'Record source': isDuplicateFlow
          ? operationLabel
          : trackableRecordSource || 'N/A'
      });
    },
    [sendEventToAmplitude, opEvents, operationLabel]
  );

  const sendSaveEditsEvent = useCallback(
    (
      result: string,
      apiLengthMs: number,
      error?: unknown,
      stepName?: string
    ) => {
      sendEventToAmplitude?.(opEvents.saveEdits, {
        [`Save ${operationLabel} result`]: `${result} (${apiLengthMs} ms)`,
        Error:
          result === 'Error' && error ? formatAmplitudeError(error) : 'N/A',
        'Save from step': getStepLabel(stepName)
      });
    },
    [sendEventToAmplitude, opEvents, operationLabel]
  );

  const sendInitiateWorkOrderToApplicationEvent = useCallback(() => {
    sendEventToAmplitude?.(awEvents.initiateWorkOrderToApplication);
  }, [sendEventToAmplitude]);

  const sendAccessEvent = useCallback(() => {
    sendEventToAmplitude?.(opEvents.access);
  }, [sendEventToAmplitude, opEvents]);

  const sendAccessEventOnDirectNavigation = useCallback(() => {
    if (!globalThis.__CW_INITIAL_PAGE_LOAD) return;

    globalThis.__CW_INITIAL_PAGE_LOAD = false;
    const navEntry = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming;
    if (navEntry?.type === 'navigate') {
      sendEventToAmplitude?.(opEvents.access);
    }
  }, [sendEventToAmplitude, opEvents]);

  const sendAccessRecordSourceEvent = useCallback(
    (recordSourceType: string, startingRecordType: OperationType) => {
      const recordSourceTypeLabel =
        APPLICATIONS_RECORD_SOURCE_LABELS[
          recordSourceType as keyof typeof APPLICATIONS_RECORD_SOURCE_LABELS
        ] || recordSourceType;

      sendEventToAmplitude?.(awEvents.accessRecordSource, {
        'Record Source type': recordSourceTypeLabel,
        'Starting Record type':
          STARTING_RECORD_TYPE_LABELS[
            startingRecordType as keyof typeof STARTING_RECORD_TYPE_LABELS
          ]
      });
    },
    [sendEventToAmplitude]
  );

  return {
    sendAccessEvent,
    sendInitiateCreateEvent,
    sendInitiateEditEvent,
    sendViewEvent,
    sendApplyFiltersEvent,
    sendDeleteEvent,
    sendInitiateDuplicateEvent,
    sendPrintPdfEvent,
    sendDownloadPdfEvent,
    sendSaveEvent,
    sendSaveEditsEvent,
    sendInitiateWorkOrderToApplicationEvent,
    sendAccessRecordSourceEvent,
    sendAccessEventOnDirectNavigation
  };
};

export default useOperationEvents;
